import axios, { type AxiosRequestConfig } from 'axios'
import ENV from '../config/env'

// Mirrors apps/mobile/src/api/client.ts:
//  - axios instance with the env-resolved baseURL
//  - request interceptor injects Bearer token + x-language header
//  - response interceptor silently refreshes on 401, then clears auth
//    and redirects if refresh itself fails.
// Storage backend is localStorage instead of AsyncStorage.

export const apiClient = axios.create({
  baseURL: ENV.API_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

console.log('[Jnati API] baseURL=', apiClient.defaults.baseURL)

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  const language = localStorage.getItem('language') ?? 'en'
  config.headers['x-language'] = language
  return config
})

// #auth-refresh-interceptor: silent access-token renewal on 401. See
// apps/mobile/src/api/client.ts for the full design comment — same
// three-part guard (per-request `_retry`, refresh URL exemption,
// raw-axios refresh call) applies here.
interface RetriableConfig extends AxiosRequestConfig {
  _retry?: boolean
}
let refreshPromise: Promise<string | null> | null = null

async function attemptRefresh(): Promise<string | null> {
  if (refreshPromise) return refreshPromise
  refreshPromise = (async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken')
      if (!refreshToken) return null
      const res = await axios.post<{ token: string; refreshToken: string }>(
        `${ENV.API_URL}/api/auth/refresh`,
        { refreshToken },
        { timeout: 10000, headers: { 'Content-Type': 'application/json' } },
      )
      const newToken = res.data?.token
      const newRefresh = res.data?.refreshToken
      if (!newToken) return null
      localStorage.setItem('token', newToken)
      if (newRefresh) localStorage.setItem('refreshToken', newRefresh)
      return newToken
    } catch (err) {
      console.warn('[Jnati API] refresh failed:', (err as Error)?.message)
      return null
    } finally {
      refreshPromise = null
    }
  })()
  return refreshPromise
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    console.warn(
      '[Jnati API]',
      error.code ?? 'ERR',
      error.config?.method?.toUpperCase(),
      error.config?.url,
      '-',
      error.message,
    )
    const status = error.response?.status
    const code = error.response?.data?.code as string | undefined
    const url = error.config?.url as string | undefined
    const config = error.config as RetriableConfig | undefined

    const isRefreshUrl = typeof url === 'string' && url.includes('/api/auth/refresh')
    if (status === 401 && config && !config._retry && !isRefreshUrl) {
      config._retry = true
      const newToken = await attemptRefresh()
      if (newToken) {
        config.headers = { ...(config.headers ?? {}), Authorization: `Bearer ${newToken}` }
        return apiClient(config)
      }
      // fall through to the session-drop path below
    }

    // 401 (unrecoverable) → token invalid; 403 + ACCOUNT_BLOCKED →
    // admin block; 403 + ACCOUNT_DELETED → self-tombstone. All three
    // drop the session; ACCOUNT_DELETED routes to a confirmation
    // screen.
    if (status === 401 || (status === 403 && (code === 'ACCOUNT_BLOCKED' || code === 'ACCOUNT_DELETED'))) {
      localStorage.removeItem('token')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('userId')
      localStorage.removeItem('user')
      const { useAuthStore } = await import('../store/authStore')
      useAuthStore.getState().clearAuth()
      // Drop the React Query cache so the next-logged-in user doesn't
      // briefly see the prior account's feed / relatives / etc.
      const { queryClient } = await import('./queryClient')
      queryClient.clear()
      // Persist the reason in sessionStorage so the login screen surfaces
      // the explicit "removed" / "suspended" notice — survives a hard
      // refresh, manual nav to /login, or stale tab restore. Cleared
      // on successful next sign-in.
      try {
        if (code === 'ACCOUNT_DELETED')      sessionStorage.setItem('jnati:accountReason', 'deleted')
        else if (code === 'ACCOUNT_BLOCKED') sessionStorage.setItem('jnati:accountReason', 'blocked')
      } catch {}
      // Hard redirect — avoids importing the router at module scope and
      // works regardless of which page the 401/403 surfaces in.
      if (!window.location.pathname.startsWith('/login')) {
        window.location.replace('/login')
      }
    }
    return Promise.reject(error)
  },
)
